/**
 * <podmuch-sticky-cta>
 * Shows a fixed mobile CTA bar once the user scrolls past a threshold.
 * Uses an IntersectionObserver against a 1px sentinel positioned at
 * `threshold` px down the document — no scroll listeners, no localStorage.
 */
class PodmuchStickyCta extends HTMLElement {
  connectedCallback() {
    const threshold = parseInt(this.getAttribute('data-threshold') || '400', 10);

    this.sentinel = document.createElement('div');
    this.sentinel.setAttribute('aria-hidden', 'true');
    Object.assign(this.sentinel.style, {
      position: 'absolute',
      top: `${threshold}px`,
      left: '0',
      width: '1px',
      height: '1px',
      pointerEvents: 'none',
    });
    document.body.appendChild(this.sentinel);

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        // Sentinel above the viewport (scrolled past) -> reveal the bar.
        this.classList.toggle('is-visible', !entry.isIntersecting);
      }
    });
    this.observer.observe(this.sentinel);

    this.link = this.querySelector('[data-pod-sticky-link]');
    this.handleClick = (event) => this.scrollToTarget(event);
    this.link?.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.sentinel?.remove();
    this.link?.removeEventListener('click', this.handleClick);
  }

  /** @param {MouseEvent} event */
  scrollToTarget(event) {
    const href = this.link?.getAttribute('href') || '';
    if (!href.startsWith('#')) return; // external URL: let the browser handle it

    const target = document.getElementById(href.slice(1));
    if (!target) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }
}

if (!customElements.get('podmuch-sticky-cta')) {
  customElements.define('podmuch-sticky-cta', PodmuchStickyCta);
}
